import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, KV, Chip } from '../components/Section';
import type { VendorMatch, VendorCategory, VendorSource } from '../types';

// Explicit ordering for the Web Stack sub-groups. Most informative first
// (the actual stack: CMS, framework, runtime), then delivery (CDN/host),
// then instrumentation, then long-tail. Categories not in this list fall
// to the end in alphabetical order.
const STACK_CATEGORY_ORDER: string[] = [
  'CMS', 'Ecommerce', 'JS Framework', 'JS Library', 'UI Framework',
  'Programming Language', 'Web Server', 'Web Framework',
  'Database', 'Cache',
  'CDN', 'Hosting', 'PaaS',
  'Tag Manager', 'Analytics',
  'Consent', 'Security',
  'SEO', 'Page builders',
  'Webmail', 'Editor',
  'Miscellaneous'
];
const STACK_RANK = new Map(STACK_CATEGORY_ORDER.map((c, i) => [c, i]));
const stackRank = (cat: string) =>
  STACK_RANK.has(cat) ? STACK_RANK.get(cat)! : STACK_CATEGORY_ORDER.length;

export function TechPage() {
  const scan = useScan();
  const tech = scan.modules.techstack;

  const techsByCat = (tech?.technologies ?? []).reduce<Record<string, NonNullable<typeof tech>['technologies']>>(
    (acc, t) => {
      (acc[t.category] = acc[t.category] ?? []).push(t);
      return acc;
    }, {});
  const techCategories = Object.entries(techsByCat).sort((a, b) => {
    const ra = stackRank(a[0]);
    const rb = stackRank(b[0]);
    return ra !== rb ? ra - rb : a[0].localeCompare(b[0]);
  });

  // True when every detected component is just CDN/Hosting/Web Server.
  // The real origin tech (CMS, framework, database, etc.) is hidden by the
  // edge — worth surfacing as a clear note rather than letting users assume
  // we found "the whole stack."
  const EDGE_ONLY = new Set(['CDN', 'Hosting', 'Web Server']);
  const onlyEdgeDetected =
    (tech?.technologies?.length ?? 0) > 0 &&
    tech!.technologies.every(t => EDGE_ONLY.has(t.category));

  return (
    <>
      <PageHeader
        title="Tech Stack & Vendors"
        subtitle="What runs the website, plus the SaaS vendors the company appears to use"
      />

      {onlyEdgeDetected && (
        <Section accent="slate">
          <Banner tone="info" title="Edge / CDN only — origin server is masked">
            The site is fronted by a CDN (Cloudflare, Fastly, etc.) which hides
            what's running on the actual origin server. The categories below
            describe how the page is <em>delivered</em>, not the underlying
            framework, CMS, or database. To see those, the operator would need
            authenticated access to the origin or the CDN would have to leak
            them in headers.
          </Banner>
        </Section>
      )}

      {tech?.error && (
        <Section accent="rose">
          <Banner tone="bad" title="Tech-stack analyzer failed">
            <span className="font-mono text-xs">{tech.error}</span>
          </Banner>
        </Section>
      )}

      {/* === Stack section: what runs the website ===
          Single Section with categories as sub-groups (matches the
          External SaaS & Vendors layout below for visual consistency). */}
      {techCategories.length === 0 ? (
        <Section title="Web Stack" accent="slate">
          <Banner tone="info" title="No technologies fingerprinted on the homepage">
            The site may server-render with version markers stripped, or be
            reachable only through JavaScript that we don't execute. This is
            common for production SPAs and CDN-fronted sites.
          </Banner>
        </Section>
      ) : (
        <Section title="Web Stack" accent="blue"
          status={{
            label: `${tech?.technologies.length ?? 0} DETECTED`,
            tone: 'info'
          }}>
          <p className="text-sm text-ink-2">
            Technologies that load on the homepage — the runtime, framework,
            CDN, and instrumentation that actually deliver the site.
          </p>
          {techCategories.map(([cat, items], i) => (
            <div key={cat} className={i === 0 ? 'mt-5' : 'mt-5'}>
              <CategoryHeader>{cat}</CategoryHeader>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {items.map(t => <TechChip key={t.name} tech={t} />)}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* === Responsible disclosure (security.txt) === */}
      <SecurityTxtCard />

      {/* === Vendor surface === */}
      <VendorSurface />
    </>
  );
}

function SecurityTxtCard() {
  const scan = useScan();
  const s = scan.modules.techstack?.security_txt;
  if (!s) return null;

  if (!s.present) {
    return (
      <Section title="Responsible Disclosure (security.txt)" accent="amber"
        status={{ label: 'NOT PUBLISHED', tone: 'warn' }}>
        <Banner tone="warn" title="No /.well-known/security.txt found">
          Publishing a <span className="font-mono">security.txt</span> (RFC 9116) gives
          researchers a clear, machine-readable way to report vulnerabilities responsibly.
          Recommended for any org that wants coordinated disclosure.
        </Banner>
      </Section>
    );
  }

  return (
    <Section title="Responsible Disclosure (security.txt)"
      accent={s.valid ? 'green' : 'amber'}
      status={{ label: s.valid ? 'VALID' : 'NEEDS ATTENTION', tone: s.valid ? 'ok' : 'warn' }}>
      <div className="grid gap-4 sm:grid-cols-2">
        <KV k="Contact" v={s.contacts.length ? s.contacts.join(', ') : '—'} />
        <KV k="Expires" v={s.expires
          ? <span className={s.expired ? 'text-rose-600' : ''}>{s.expires}{s.expired ? ' (expired)' : ''}</span>
          : '—'} />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Chip tone={s.has_policy ? 'ok' : 'mute'}>Policy {s.has_policy ? '✓' : '—'}</Chip>
        <Chip tone={s.has_encryption ? 'ok' : 'mute'}>Encryption {s.has_encryption ? '✓' : '—'}</Chip>
        <Chip tone={s.has_canonical ? 'ok' : 'mute'}>Canonical {s.has_canonical ? '✓' : '—'}</Chip>
      </div>
      {s.issues.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-amber-700">
          {s.issues.map((iss, i) => <li key={i}>• {iss}</li>)}
        </ul>
      )}
    </Section>
  );
}

function TechChip({ tech }: { tech: { name: string; version?: string; confidence?: number } }) {
  // Backend marks dependency-inferred tech (e.g. React from Next.js) with
  // confidence < 90 vs 90 for directly-observed. Distinguish them visually.
  const inferred = tech.confidence != null && tech.confidence < 90;
  return (
    <div
      title={inferred ? 'Inferred from a detected dependency, not directly observed' : undefined}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm shadow-xs ${
        inferred ? 'border-line bg-panel-2' : 'border-line bg-panel'
      }`}
    >
      <span className={`font-medium ${inferred ? 'text-ink-2' : 'text-ink'}`}>{tech.name}</span>
      {tech.version && (
        <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-ink-2">
          v{tech.version}
        </span>
      )}
      {inferred && (
        <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ink-3">
          inferred
        </span>
      )}
    </div>
  );
}

function VendorSurface() {
  const scan = useScan();
  const v = scan.modules.vendors;

  if (!v) return null;

  const empty = !v.email_platform && v.vendors.length === 0;
  if (empty) {
    return (
      <Section title="External SaaS & Vendors" accent="slate">
        <Banner tone="info" title="No external vendor signals detected">
          We didn't see any vendor verifications in your DNS, recognizable
          email-platform MX records, or links to known SaaS hosts on your
          homepage.
        </Banner>
      </Section>
    );
  }

  // Group vendors by category for display.
  const byCat = v.vendors.reduce<Record<VendorCategory, VendorMatch[]>>((acc, m) => {
    (acc[m.category] = acc[m.category] ?? []).push(m);
    return acc;
  }, {} as Record<VendorCategory, VendorMatch[]>);

  const orderedCats: VendorCategory[] = [
    'Email', 'Productivity', 'Communication', 'Storage', 'Identity',
    'Security', 'CRM', 'Developer', 'Marketing', 'Payments', 'Other'
  ];
  // Derive from the categories actually present so NO backend category is ever
  // silently dropped (previously 'Email' vendors vanished but still counted in total).
  const catRank = (c: VendorCategory) => {
    const i = orderedCats.indexOf(c);
    return i === -1 ? orderedCats.length : i;
  };
  const renderCats = (Object.keys(byCat) as VendorCategory[])
    .filter(c => byCat[c]?.length)
    .sort((a, b) => catRank(a) - catRank(b));

  return (
    <Section title="External SaaS & Vendors" accent="blue"
      status={{ label: `${v.total} DETECTED`, tone: 'info' }}>
      <p className="text-sm text-ink-2">
        Inferred from your MX records, TXT-record vendor verifications, homepage
        link patterns, and operational subdomains. These are SaaS vendors the
        company appears to use — not technologies running the website.
      </p>

      {/* Email platform — always its own row */}
      {v.email_platform && (
        <div className="mt-5">
          <CategoryHeader>Email Platform</CategoryHeader>
          <div className="mt-1.5">
            <VendorChip vendor={v.email_platform} />
          </div>
        </div>
      )}

      {/* Other vendor categories */}
      {renderCats.map(cat => (
        <div key={cat} className="mt-5">
          <CategoryHeader>{cat}</CategoryHeader>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {byCat[cat].map(vd => <VendorChip key={vd.name} vendor={vd} />)}
          </div>
        </div>
      ))}
    </Section>
  );
}

function CategoryHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">
      {children}
    </p>
  );
}

function VendorChip({ vendor }: { vendor: VendorMatch }) {
  const sourceLabel: Record<VendorSource, string> = {
    mx:        'MX',
    txt:       'TXT',
    link:      'Link',
    subdomain: 'Sub'
  };
  const sourceBg: Record<VendorSource, string> = {
    txt:       'bg-sev-ok/15 text-sev-ok',
    mx:        'bg-sev-low/15 text-sev-low',
    subdomain: 'bg-sev-med/15 text-sev-med',
    link:      'bg-slate-200 text-ink-2'
  };
  return (
    <div
      title={vendor.evidence}
      className="inline-flex items-center gap-2 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm shadow-xs"
    >
      <span className="font-medium text-ink">{vendor.name}</span>
      <span className={`rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase ${sourceBg[vendor.source]}`}>
        {sourceLabel[vendor.source]}
      </span>
    </div>
  );
}
